import { Tag } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useOverheadCategories } from "@/hooks/use-overhead-categories";
import { OverheadCategoryRow } from "./OverheadCategoryRow";

interface OverheadCategoryLibraryProps {
  isAdmin: boolean;
}

export function OverheadCategoryLibrary({ isAdmin }: OverheadCategoryLibraryProps) {
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);

  const {
    data: categories = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useOverheadCategories({
    search: debouncedSearch || undefined,
    includeInactive,
  });

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load overhead categories"}
        onRetry={() => refetch()}
      />
    );
  }

  const tableHeaders = [
    { label: "Code", className: "w-28" },
    { label: "Label" },
    { label: "Subcategories", className: "w-36" },
    { label: "Status", className: "w-28" },
    ...(isAdmin ? [{ label: "Actions", className: "w-32 text-right" }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="h-section">Overhead Categories</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Non-billable overhead codes (A001–A015) used to classify overhead time entries.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search by code or label…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 rounded border border-input"
          />
          Show inactive
        </label>
      </div>

      <DataTable
        headers={tableHeaders}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Tag}
            title="No overhead categories"
            description={
              search ? "No categories match your search." : "No overhead categories found."
            }
          />
        }
      >
        {categories.map((category) => (
          <OverheadCategoryRow key={category.id} category={category} isAdmin={isAdmin} />
        ))}
      </DataTable>
    </div>
  );
}
